import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "AI Clean Core didn't just help us implement AI—they liberated us from vendor dependency. We now control our AI destiny and have saved over $2.3M annually while gaining capabilities our competitors can't match.",
      author: "Michael Thompson",
      role: "CTO",
      company: "TechFlow Industries",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      metrics: {
        savings: "$2.3M annually",
        performance: "40% faster deployment",
        independence: "100% vendor-free AI stack"
      }
    },
    {
      quote: "The transformation was remarkable. We went from being held hostage by proprietary AI vendors to having complete control over our machine learning pipeline. Our internal team now innovates at a pace that was impossible before.",
      author: "Dr. Lisa Rodriguez",
      role: "VP of Data Science",
      company: "Global Manufacturing Corp",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      metrics: {
        savings: "85% cost reduction",
        performance: "3x faster innovation",
        independence: "Zero vendor lock-in"
      }
    },
    {
      quote: "AI Clean Core's approach to building internal capabilities was game-changing. Instead of outsourcing our AI thinking, we now have a world-class internal AI team that understands our business better than any vendor ever could.",
      author: "James Park",
      role: "Chief Innovation Officer",
      company: "Financial Services United",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      metrics: {
        savings: "$1.8M in first year",
        performance: "60% improvement in model accuracy",
        independence: "Complete internal ownership"
      }
    }
  ];

  const impactStats = [
    {
      icon: "DollarSign",
      value: "$47M+",
      label: "Total Client Savings",
      description: "Cumulative cost savings across all client engagements"
    },
    {
      icon: "TrendingUp",
      value: "340%",
      label: "Average ROI",
      description: "Return on investment within 18 months"
    },
    {
      icon: "Clock",
      value: "65%",
      label: "Faster Innovation",
      description: "Reduced time-to-market for AI initiatives"
    },
    {
      icon: "Shield",
      value: "100%",
      label: "Vendor Independence",
      description: "Complete elimination of vendor lock-in"
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-brand-primary/10 rounded-full px-4 py-2 mb-6">
            <Icon name="MessageSquare" size={16} className="text-brand-primary" />
            <span className="text-sm font-medium text-brand-primary">Client Success Stories</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
            Transformational Impact
            <span className="block text-brand-primary">Beyond Expectations</span>
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Our clients don't just achieve AI independence—they gain competitive advantages that compound over time.
          </p>
        </div>

        {/* Testimonials */}
        <div className="space-y-8 mb-20">
          {testimonials?.map((testimonial, index) => (
            <div key={index} className="bg-brand-surface rounded-xl p-8 lg:p-12 shadow-executive">
              <div className="grid lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-2">
                  <div className="mb-6">
                    <Icon name="Quote" size={32} className="text-brand-accent mb-4" />
                    <blockquote className="text-lg lg:text-xl text-text-primary leading-relaxed italic">
                      "{testimonial?.quote}"
                    </blockquote>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <Image
                      src={testimonial?.image}
                      alt={testimonial?.author}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-semibold text-text-primary">
                        {testimonial?.author}
                      </div>
                      <div className="text-brand-primary font-medium">
                        {testimonial?.role}
                      </div>
                      <div className="text-text-secondary">
                        {testimonial?.company}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="lg:col-span-1">
                  <h4 className="font-semibold text-text-primary mb-4">Key Results</h4>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Icon name="DollarSign" size={16} className="text-green-600" />
                      <span className="text-sm text-text-secondary">{testimonial?.metrics?.savings}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="TrendingUp" size={16} className="text-blue-600" />
                      <span className="text-sm text-text-secondary">{testimonial?.metrics?.performance}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Shield" size={16} className="text-brand-primary" />
                      <span className="text-sm text-text-secondary">{testimonial?.metrics?.independence}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Impact Statistics */}
        <div className="bg-gradient-to-br from-brand-primary to-blue-800 rounded-2xl p-8 lg:p-12 text-white">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              Measurable Impact Across All Clients
            </h3>
            <p className="text-blue-100 text-lg">
              These aren't just success stories—they're the new standard
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats?.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={stat?.icon} size={32} className="text-brand-accent" />
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-brand-accent mb-2">
                  {stat?.value}
                </div>
                <div className="text-white font-medium mb-2">
                  {stat?.label}
                </div>
                <p className="text-blue-100 text-sm">
                  {stat?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;